import React, { useState, useRef, useEffect } from 'react';
import { useSpring, animated } from 'react-spring/web.cjs';
import { Link } from 'react-router-dom';
import { oneLine } from 'common-tags/es';
import { isString } from 'lodash/lang';

import { LottieAnimation } from './animation';
import PositionedDiv from './positioned-div';
import Slider from './slider';
import Img from './img';
import Svg from './svg';
import {trackEvent} from "../utils/analytics";

const DialogButton = ({ index, isActive, onClick }) => (
    <button
        type='button'
        className={ `rounded w-3 h-3 mr-2 ${ isActive ? 'bg-orange' : 'bg-grey' }` }
        onClick={ onClick }
    >
        <span className='hidden'>{ index }</span>
    </button>
);

const DialogBox = ({ title, position, content, icon, link, isOpen, setClosed, dialogRef }) => {
    const boxClassName = oneLine`
        tiles__dialog-box fixed h-100 w-100 px-5 sm:h-auto sm:px-0 sm:absolute sm:w-72
        flex flex-jc-around flex-column flex-ai-center ${ position } ${ isOpen ? 'z-20' : '' }
        `;
    const innerClassName = oneLine`
        tiles__dialog-box-inner top w-100
        px-2 py-1 rounded text-center
        `;
    const props = useSpring({ display: isOpen ? 'flex' : 'none' });
    return (
        <animated.div className={ boxClassName } style={ props }>
            <div ref={ dialogRef } className={ innerClassName }>
                <p className='text-right'>
                    <button type='button' className='py-1 px-2' onClick={ setClosed }>
                        <Svg name='close' width='10px' height='10px' className='color-grey' />
                    </button>
                </p>
                <div className='p5'>
                    <h2 className='color-orange mb-4'>{ title }</h2>
                    { icon && (
                        <Img
                            filename={ icon.filename }
                            style={{ width: icon.width }}
                            alt={ icon.alt }
                        />
                    ) }
                    { isString(content) ? (
                        <p className={ `${ link ? '' : 'mb-4' }` } dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                        <Slider button={ DialogButton } className='my-2'>
                            { content.map((value, key) => (
                                <p className='my-4' key={ key } dangerouslySetInnerHTML={{ __html: value }} />
                            )) }
                        </Slider>
                    ) }
                    { link && (
                        <p className='my-4'>
                            <Link to={ link } className='underline-link font-semibold color-orange'>
                                Read more
                            </Link>
                        </p>
                    ) }
                </div>
            </div>
            <div className='arrow' />
        </animated.div>
    );
};

const Dialog = ({ dialog, currentDialog, setDialog, unsetDialog }) => {
    const { title, position = 'top', top, left, mobileTop, mobileLeft } = dialog;
    const key = `${ title }-${ top }-${ left }`;
    const isOpen = currentDialog === key;

    const dialogRef = useRef();
    const dialogButtonRef = useRef();

    const handleOutsideClick = function(e) {
        if (dialogRef.current && dialogRef.current.contains(e.target)) {
            document.addEventListener('mousedown', handleOutsideClick.bind(this), {once: true});
            return;
        }
        unsetDialog();
    };

    const setClosed = () => {
        unsetDialog();
        document.removeEventListener('mousedown', handleOutsideClick.bind(key), {once: true});
        trackEvent('close', 'dialog', key);
    };
    const setOpen = () => {
        setDialog(`${ title }-${ top }-${ left }`);
        document.addEventListener('mousedown', handleOutsideClick.bind(key), {once: true});
        trackEvent('open', 'dialog', key);
    };

    const toggleOpen = () => {
        if (isOpen) {
            setClosed();
        } else {
            setOpen();
        }
    };

    const reversePositionMap = {
        top: 'bottom',
        bottom: 'top',
    };

    return (
        <PositionedDiv
            className='tiles__point-of-interest'
            top={ top }
            left={ left }
            mobileTop={ mobileTop }
            mobileLeft={ mobileLeft }
        >
            <DialogBox
                title={ title }
                position={ position }
                icon={ dialog.icon }
                content={ dialog.content }
                link={ dialog.link }
                isOpen={ isOpen }
                setClosed={ setClosed }
                dialogRef={ dialogRef }
            />
            { dialog.showTitle && (
                <div
                    className={ oneLine`
                    tiles__point-of-interest__text
                    ${ reversePositionMap[position] }
                    ${ dialog.first ? 'first' : '' }
                    ` }
                >
                    <h6 className='color-dark-blue'>{ dialog.title }</h6>
                    { dialog.first && (
                        <>
                            <span className='color-dark-blue md:hidden'>Tap here to begin</span>
                            <span className='color-dark-blue hidden md:block'>Click here to begin</span>
                        </>
                    ) }
                </div>
            ) }
            <button
                className='tiles__point-of-interest__button z-10 relative'
                type='button'
                onClick={ toggleOpen }
                ref={ dialogButtonRef }
            >
                { dialog.number && (
                    <div className={
                        oneLine`
                        tiles__point-of-interest__number text-base md:text-2xl font-semibold 
                        text-center color-white h-100 w-100`
                    }
                    >
                        { dialog.number }
                    </div>
                ) }
                <LottieAnimation filename='2137-pulse.json' loop={ true } top={ 0 } overrideAnimationContainer={ dialogButtonRef }/>
            </button>
        </PositionedDiv>
    );
};

export default Dialog;
